import { useApiRequest } from "@lib/hooks";
import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";
import { useEffect, useState } from "react";

import { useContainer } from "@src/contexts/container-context";
import { useCurrentUser } from "@src/hooks/queries/account";
import { useRole } from "@src/hooks/queries/account-selectors";

import { ProfessionalLearningProduct } from "@components/custom/dropdowns/product-dropdown-list";

import {
  exportOrganizationProgramStats,
  getProgramOrganizationDailyStats,
  getProgramOrganizationStats as getProgramOrganizationGeneralStats,
  getStatsFileName,
} from "@services";
import { DashboardBanner, ProgramInsights } from "@views/dashboard/components";

import "./program.tab.scss";

export const ProgramTab = ({ professionalLearning = false }) => {
  const defaultProgramOption = { displayName: "All Programs" };
  const defaultOrgOption = { displayName: "All Organizations" };
  const { organizationService, courseService } = useContainer();

  const currentUser = useCurrentUser();
  const role = useRole();
  const organizationName = currentUser?.organizationName;

  const { organizationCode, userId } = currentUser;

  const [dateRange, setDateRange] = useState({});
  const [programSelected, setProgramSelected] = useState(defaultProgramOption);
  const [organizationSelected, setOrganizationSelected] =
    useState(defaultOrgOption);

  const [generalStats, setGeneralStats] = useState({ isLoading: true });
  const [dailyStats, setDailyStats] = useState({ isLoading: true });

  const { loading: downloadingFile, sendRequest: exportInsights } =
    useApiRequest(() => {
      const selectedName = organizationSelected?.code
        ? organizationSelected.name
        : organizationName;
      const orgCode = organizationSelected?.code ?? organizationCode;
      const fileName = getStatsFileName(selectedName, dateRange);
      const filters = {
        dateRange,
        ...(professionalLearning && { professional_learning: true }),
      };

      return exportOrganizationProgramStats({
        organizationService,
        role,
        orgCodeOrUserId: orgCode,
        filters,
        fileName,
        getMultiOrg: !organizationSelected?.code,
      });
    }, false);

  useUnloadWarning({ trigger: downloadingFile });

  const getOrganizationStats = (orgCode, filters, isMultiOrg) => {
    Promise.all([
      getProgramOrganizationGeneralStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
        getMultiOrg: isMultiOrg,
      }).then((response) => setGeneralStats({ isLoading: false, response })),
      getProgramOrganizationDailyStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
        getMultiOrg: isMultiOrg,
      }).then((response) => setDailyStats({ isLoading: false, response })),
    ]).catch(() => {
      setGeneralStats({ isLoading: false });
      setDailyStats({ isLoading: false });
    });
  };

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    const programId = programSelected?.programId ?? undefined;
    const orgCode = organizationSelected?.code ?? organizationCode;
    const isSingleOrg =
      organizationSelected?.displayName !== "All Organizations" &&
      !!organizationSelected?.code;

    const filters = {
      dateRange,
      programId,
      ...(professionalLearning && { professional_learning: true }),
    };
    if (startDate && endDate) {
      getOrganizationStats(orgCode, filters, !isSingleOrg);
    }
  }, [dateRange, organizationSelected, programSelected]);

  const onApplyFilters = ({ dateRange, organization, program }) => {
    const { startDate, endDate } = dateRange || {};
    clearStats();
    setDateRange({ startDate, endDate });
    setOrganizationSelected(organization);
    setProgramSelected(program);
  };

  const clearStats = () => {
    setDailyStats({ isLoading: true });
    setGeneralStats({ isLoading: true });
  };

  const onExportStatsData = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      exportInsights();
    }
  };

  // Cancel ongoing requests when changing tab
  useEffect(() => () => courseService.cancelRequest(), []);

  return (
    <div className="program-single-tab">
      <DashboardBanner
        description={
          professionalLearning
            ? ProfessionalLearningProduct.description
            : undefined
        }
        product={Product.PROG}
      />
      <ProgramInsights
        userInfo={{ userId, role }}
        orgCode={organizationSelected?.code ?? organizationCode}
        programFilterEnabled
        organizationFilterEnabled={true}
        generalStats={generalStats?.response}
        dailyStats={dailyStats}
        onApplyFilters={onApplyFilters}
        onExportData={onExportStatsData}
        exportDataDisabled={downloadingFile}
        professionalLearning={professionalLearning}
      />
      <div className="notification-loader-container">
        {downloadingFile && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download of{" "}
            <strong>{`${
              organizationSelected?.code
                ? organizationSelected.name
                : organizationName
            }'s`}</strong>{" "}
            insights. Please wait...
          </Notification>
        )}
      </div>
    </div>
  );
};
